import type { Region } from '@/api'
import { copy } from '@/copy'

// The leaderboard's arithmetic (ADR-0029): shares are coarse, multiples of
// step, and drawn as bars scaled to the leader, never as numbers; screen
// readers hear each in words.
export const step = 5

/** The leaders: every region with the top share, none while nobody has voted. */
export function leaders(regions: Region[]): string[] {
  const top = Math.max(0, ...regions.map((r) => r.share))
  return top === 0 ? [] : regions.filter((r) => r.share === top).map((r) => r.id)
}

/** A bar's width, as a percent of the leader's. */
export function width(share: number, regions: Region[]): number {
  const top = Math.max(0, ...regions.map((r) => r.share))
  return top === 0 ? 0 : (100 * share) / top
}

/**
 * The regions this account voted for that the shares do not show yet. A
 * region with any vote has a share of at least step, so a voted region at 0
 * means the shares are older than the vote: the account host answers a vote
 * with its cached shares, and the console reads them again while any are
 * missing (useRegions).
 */
export function missing(regions: Region[] | undefined): string[] {
  return (regions ?? []).filter((r) => r.voted && r.share <= 0).map((r) => r.id)
}

/** Whether the board is catching up with this account's votes. */
export function catchingUp(regions: Region[] | undefined): boolean {
  return missing(regions).length > 0
}

/**
 * The line under the board: while it is catching up, that the vote is in,
 * never that nobody has voted, whatever else it holds; late once the console
 * has stopped asking.
 */
export function note(regions: Region[], late = false): string {
  if (catchingUp(regions)) return late ? copy.regions.catchingUpLate : copy.regions.catchingUp
  const top = leaders(regions)
  if (top.length === 0) return copy.regions.empty
  if (top.length > 1) return copy.regions.tie
  return copy.regions.leading(regions.find((r) => r.id === top[0])!.name)
}

/** The board's order: by share, ties in the list's order. */
export function ranked(regions: Region[]): string[] {
  return regions
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r.share - a.r.share || a.i - b.i)
    .map(({ r }) => r.id)
}

const parts: [number, string][] = [
  [10, 'a tenth'],
  [12.5, 'an eighth'],
  [16.7, 'a sixth'],
  [20, 'a fifth'],
  [25, 'a quarter'],
  [33.3, 'a third'],
  [40, 'two fifths'],
  [50, 'half'],
  [60, 'three fifths'],
  [66.7, 'two thirds'],
  [75, 'three quarters'],
  [80, 'four fifths'],
  [90, 'nine tenths'],
]

/** A share in words: "about a third of the votes". A vote of this account's that the cached shares do not show yet is "your vote is in". */
export function words(share: number, voted = false): string {
  if (share <= 0) return voted ? 'your vote is in' : 'no votes yet'
  if (share >= 100) return 'every vote so far'
  if (share <= step) return 'a sliver of the votes'
  let best = parts[0]
  for (const p of parts) if (Math.abs(p[0] - share) < Math.abs(best[0] - share)) best = p
  return `about ${best[1]} of the votes`
}
